// Countdown clock on a promo (inc_promo.html), in two phases, and the two phases count
// in different units on purpose:
//
//   1. Before the promo's Start Date -- counts down TO the start in whole days,
//      "5 days Until Sale!", switching to a clock for the last day. This is the
//      pre-sale teaser, where the exact minute doesn't matter yet.
//   2. Once the start has passed and the sale is live -- counts down to the End Date as
//      a clock the whole way, "58H:20M:15S Left to Save!", never in days. Fischer asked
//      for this explicitly: the running sale reads as urgent only if the number visibly
//      moves, and "2 days" sits still for 24 hours at a stretch.
//
// Each field of that clock carries its unit letter (H/M/S) rather than standing alone as
// "58:20:15", which is the exact form Fischer asked for -- an unlabelled clock past 24
// hours reads as a runtime or a score, not as time left. Hours in phase 2 accumulate past
// 24 instead of rolling into a day count, so a sale opening 9am Saturday and ending
// Monday night starts out at "62H:59M:00S", not "2 days".
//
// The element removes itself once nothing is left to count down to. Each phase's
// trailing text is its own Promos Special Content field (countdown_text and
// countdown_end_text), falling back to "Until Sale!" / "Left to Save!".
//
// The phase is re-decided on every tick, not once at setup, so a visitor sitting on the
// page when the sale opens sees the clock roll straight from "00:00:01 Until Sale!" into
// the End Date countdown without a reload.
//
// A promo may leave either date blank ("open ended" on that side). A blank Start Date
// means the sale is already live, so phase 1 is skipped; a blank End Date means the
// element removes itself when the start passes, since there is no later moment to
// count toward.
//
// "Day" here means calendar day, not a rolling 24-hour window -- the two are different.
// A target is a real moment in time (e.g. 4:18pm), so the switch from days to a clock
// happens at LOCAL MIDNIGHT on its calendar day, however many hours that still leaves on
// the clock (up to just under 24), not exactly 24 hours before 4:18pm. inc_promo.html's
// own {% if %} only keeps this out of the markup entirely once BOTH dates are in the past
// -- everything below is what keeps it correct for a visitor already on the page,
// including the exact instant each phase ends.
var countdownEls = document.querySelectorAll('[data-promo-countdown-start], [data-promo-countdown-end]');

// "1 Hour", "58 Hours" -- the unit name is singular only at exactly 1.
function unit(value, name) {
    return value + ' ' + name + (value === 1 ? '' : 's');
}

// Two-digit clock field. Hours are padded the same as minutes and seconds so the whole
// countdown keeps one width as it ticks, and are NOT capped at two digits -- a promo
// running longer than 99 hours widens to "104H" rather than wrapping around.
function pad2(n) {
    return n < 10 ? '0' + n : String(n);
}

// A data attribute holding an ISO 8601 moment, or null when it is absent, empty (the
// Promos row left that date blank, so Django rendered nothing), or unparseable.
function parseTarget(value) {
    if (!value) return null;
    var parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
}

// Whole calendar days between two Dates, in whichever timezone this code is running in
// (the visitor's own browser) -- comparing Y/M/D directly rather than dividing elapsed
// milliseconds by a day's worth, which would be thrown off by the partial day on each end
// and by DST crossings.
function calendarDaysBetween(from, to) {
    var fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    var toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.round((toDay - fromDay) / 86400000);
}

function formatCountdown(now, target, showSeconds, suffix, clockOnly) {
    var daysUntil = clockOnly ? 0 : calendarDaysBetween(now, target);

    if (daysUntil >= 1) {
        return unit(daysUntil, 'Day') + ' ' + suffix;
    }

    // An HH:MM(:SS) clock counting down to the exact moment, each field labelled with its
    // unit. `hours` deliberately is not reduced modulo 24, so a clockOnly phase reads
    // "62H:59M:00S" rather than restarting at "14H" each midnight. In the days phase this
    // branch is only reached once the target is inside today, so it never exceeds 23 there
    // anyway. Every field is kept even at zero ("00H:00M:30S"), unlike a spelled-out
    // phrase -- dropping one would make the countdown jump width as it ticks.
    var totalSeconds = Math.floor((target.getTime() - now.getTime()) / 1000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var clock = pad2(hours) + 'H:' + pad2(minutes) + 'M';

    if (showSeconds) {
        clock += ':' + pad2(totalSeconds % 60) + 'S';
    }

    return clock + ' ' + suffix;
}

// Which moment this element is counting toward right now, and the text that goes with it.
// The start wins while it is still ahead (pre-sale teaser), then the end takes over
// (sale is live). Returns null once neither is ahead, meaning there is nothing left to
// show.
function currentPhase(now, config) {
    if (config.start && config.start.getTime() > now.getTime()) {
        return { target: config.start, suffix: config.startText, clockOnly: false };
    }

    if (config.end && config.end.getTime() > now.getTime()) {
        return { target: config.end, suffix: config.endText, clockOnly: true };
    }

    return null;
}

// Returns false once both dates have passed, so the caller knows to stop ticking.
function tick(el, config) {
    var now = new Date();
    var phase = currentPhase(now, config);

    if (!phase) {
        el.remove();
        return false;
    }

    var text = formatCountdown(now, phase.target, config.showSeconds, phase.suffix, phase.clockOnly);
    if (el.textContent !== text) el.textContent = text;
    return true;
}

countdownEls.forEach(function (el) {
    var config = {
        start: parseTarget(el.dataset.promoCountdownStart),
        end: parseTarget(el.dataset.promoCountdownEnd),
        showSeconds: el.dataset.promoCountdownSeconds === 'true',
        // The template already defaults these to "Until Sale!" / "Left to Save!" when the
        // Promo leaves countdown_text / countdown_end_text unset, but a countdown element
        // inserted without those data attributes at all (e.g. hand-authored markup) still
        // gets the same defaults here rather than rendering a blank suffix.
        startText: el.dataset.promoCountdownText || 'Until Sale!',
        endText: el.dataset.promoCountdownEndText || 'Left to Save!',
    };

    // No guard for "neither date parsed" needed: currentPhase() returns null for that,
    // so this first tick() removes the element rather than leaving an empty block span
    // behind still carrying .count-down's bottom margin.
    if (!tick(el, config)) return;

    // Always ticks once a second, seconds display or not -- the alternative is having to
    // restart the interval at a different cadence the moment the days-vs-clock phase (or a
    // future seconds toggle) changes, and a second's worth of idle arithmetic on one small
    // element is not a real cost. tick() only touches the DOM when the text actually
    // changes, so this doesn't mean redrawing every second while still in the days phase.
    var interval = setInterval(function () {
        if (!tick(el, config)) clearInterval(interval);
    }, 1000);
});
